import React from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  styled,
  alpha,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Slideshow as PowerPointIcon,
  Email as EmailIcon,
  AttachFile as AttachIcon,
  RemoveCircle as DetachIcon,
  OpenInNew as OpenIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';

interface DocumentCardProps {
  document: {
    title: string;
    type: string;
    preview?: string;
    relevance: string | number;
    source: string;
    url?: string;
    attachment_reason?: string;
    document_node_id?: string;
  };
  isAttached?: boolean;
  onAttach?: (documentId: string) => void;
  onDetach?: (documentId: string) => void;
  onView?: (url: string) => void;
  compact?: boolean;
}

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-1px)',
  },
}));

const RelevanceChip = styled(Chip)<{ relevancescore: number }>(({ theme, relevancescore }) => {
  const getColor = (score: number) => {
    if (score >= 0.8) return theme.palette.success.main;
    if (score >= 0.6) return theme.palette.warning.main;
    if (score >= 0.4) return theme.palette.info.main;
    return theme.palette.error.main;
  };

  return {
    backgroundColor: alpha(getColor(relevancescore), 0.1),
    color: getColor(relevancescore),
    borderColor: getColor(relevancescore),
    border: `1px solid ${getColor(relevancescore)}`,
  };
});

const getDocumentIcon = (type: string) => {
  const iconMap: Record<string, React.ReactElement> = {
    'PDF': <PdfIcon />,
    'Word Document': <DescriptionIcon />,
    'Excel Spreadsheet': <ExcelIcon />,
    'PowerPoint': <PowerPointIcon />,
    'Email': <EmailIcon />,
    'Document': <DescriptionIcon />,
  };
  return iconMap[type] || <DescriptionIcon />;
};

const getRelevanceScore = (relevance: string | number): number => {
  if (typeof relevance === 'number') return relevance;

  const relevanceMap: Record<string, number> = {
    'High': 0.8,
    'Medium': 0.6,
    'Low': 0.4,
    'Very Low': 0.2,
  };

  return relevanceMap[relevance] || 0.5;
};

const DocumentCard: React.FC<DocumentCardProps> = ({
  document,
  isAttached = false,
  onAttach,
  onDetach,
  onView,
  compact = false,
}) => {
  const relevanceScore = getRelevanceScore(document.relevance);
  const documentIcon = getDocumentIcon(document.type);

  const handleAttach = () => {
    if (document.document_node_id && onAttach) {
      onAttach(document.document_node_id);
    }
  };

  const handleDetach = () => {
    if (document.document_node_id && onDetach) {
      onDetach(document.document_node_id);
    }
  };

  const handleView = () => {
    if (document.url && onView) {
      onView(document.url);
    }
  };

  return (
    <StyledCard variant="outlined">
      <CardContent style={{ paddingBottom: compact ? 8 : 16 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <div style={{
            color: '#325BA8',
            display: 'flex',
            alignItems: 'center',
            marginTop: 4,
          }}>
            {documentIcon}
          </div>

          <div style={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              style={{
                fontWeight: 600,
                marginBottom: 4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {document.title}
            </Typography>

            <Stack direction="row" spacing={1} style={{ marginBottom: 8 }}>
              <RelevanceChip
                label={typeof document.relevance === 'string' ? document.relevance : `${Math.round(relevanceScore * 100)}%`}
                size="small"
                variant="outlined"
                relevancescore={relevanceScore}
              />
              <Chip
                label={document.source}
                size="small"
                variant="outlined"
                color="primary"
              />
              <Chip
                label={document.type}
                size="small"
                variant="outlined"
              />
            </Stack>

            {!compact && document.preview && (
              <Typography
                variant="body2"
                color="text.secondary"
                style={{
                  fontSize: '0.875rem',
                  lineHeight: 1.4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {document.preview}
              </Typography>
            )}

            {document.attachment_reason && (
              <Typography
                variant="caption"
                color="text.secondary"
                style={{
                  display: 'block',
                  marginTop: 8,
                  fontStyle: 'italic',
                }}
              >
                {document.attachment_reason}
              </Typography>
            )}
          </div>

          <Stack direction="column" spacing={0.5}>
            {isAttached ? (
              <Tooltip title="Attached to task">
                <StarIcon color="primary" fontSize="small" />
              </Tooltip>
            ) : (
              <Tooltip title="Not attached">
                <StarBorderIcon color="disabled" fontSize="small" />
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </CardContent>

      <CardActions style={{ paddingTop: 0, paddingLeft: 16, paddingRight: 16, paddingBottom: 16 }}>
        <Stack direction="row" spacing={1} style={{ width: '100%' }}>
          {document.url && (
            <Tooltip title="View document">
              <IconButton
                size="small"
                onClick={handleView}
                color="primary"
              >
                <OpenIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {isAttached ? (
            <Tooltip title="Detach from task">
              <IconButton
                size="small"
                onClick={handleDetach}
                color="error"
                disabled={!onDetach}
              >
                <DetachIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Attach to task">
              <IconButton
                size="small"
                onClick={handleAttach}
                color="primary"
                disabled={!onAttach}
              >
                <AttachIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </CardActions>
    </StyledCard>
  );
};

export default DocumentCard;